import React from 'react';

import isEqual from 'fast-deep-equal';
import { isEmpty, pick } from 'lodash/fp';

import api from '../../api';
import { SmartOptionResource } from '../../types';
import { parseApiError } from '../../utility/api-helpers';
import CreateSmartOptionModalView from './CreateSmartOptionModalView';

const EMPTY_DATA = {
    name: undefined,
    description: undefined,
    filter: undefined,
    filter_values: undefined,
};

const OPTIONAL_DATA_PROPS = ['description'];

export type FormData = Pick<SmartOptionResource, keyof typeof EMPTY_DATA>;
export type FormErrors = Record<keyof FormData, string> & { server?: string };

const defaultProps = {
    isVisible: false,
    onSubmit: () => {},
    createResource: api.settings.createSmartOption,
};

type Props = {
    resource?: Partial<SmartOptionResource>;
    createResource?: typeof defaultProps.createResource;
    isVisible: boolean;
    onClose: () => void;
    onSubmit?: (resource: SmartOptionResource) => void;
};

type State = {
    formData: FormData;
    errors: FormErrors | null;
    isSubmitting: boolean;
};

const getStateFromProps = ({ resource }: Props): State => ({
    formData: resource == null ? EMPTY_DATA : pick(Object.keys(EMPTY_DATA), resource),
    errors: null,
    isSubmitting: false,
});

class CreateSmartOptionModal extends React.PureComponent<Props, State> {
    static defaultProps = defaultProps;

    state: State = getStateFromProps(this.props);

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { isVisible } = this.props;
        if (prevProps.isVisible !== isVisible && isVisible) {
            this.setState(getStateFromProps(this.props));
        }
    }

    handleChange = (e) => {
        const { name, value } = e.target;

        this.setState(({ formData }) => ({ formData: { ...formData, [name]: value } }));
    };

    isSubmittable = (): boolean => {
        const { formData } = this.state;
        const { resource } = this.props;

        const formDataKeys = Object.keys(formData).filter(
            (key) => !OPTIONAL_DATA_PROPS.includes(key)
        );
        const allRequiredValuesAreNotEmpty = !formDataKeys.find((key) => isEmpty(formData[key]));

        if (!allRequiredValuesAreNotEmpty) {
            return false;
        }

        if (resource) {
            return !!formDataKeys.find((key) => !isEqual(formData[key], resource[key]));
        }

        return true;
    };

    handleSubmit = (data?) => {
        const { isSubmitting, formData } = this.state;
        const { createResource, resource, onSubmit } = this.props;

        if (isSubmitting) {
            return;
        }

        this.setState({ isSubmitting: true });

        const payload = { ...resource, ...formData };

        createResource(payload)
            .then((resource) => {
                onSubmit && onSubmit(resource);
                this.handleClose();
            })
            .catch(this.handleError)
            .finally(() => this.setState({ isSubmitting: false }));
    };

    handleError = (err) =>
        this.setState({
            errors: parseApiError(err),
        });

    handleClose = () => {
        this.props.onClose();
    };

    render() {
        const { isVisible } = this.props;
        const { formData, errors, isSubmitting } = this.state;

        return (
            <CreateSmartOptionModalView
                formData={formData}
                errors={errors}
                onSubmit={this.handleSubmit}
                onClose={this.handleClose}
                onChange={this.handleChange}
                isSubmittable={this.isSubmittable()}
                isSubmitting={isSubmitting}
                isVisible={isVisible}
                localePrefix="smart_options_field_"
            />
        );
    }
}

export default CreateSmartOptionModal;
